import { useQuery } from "@tanstack/react-query";
import { get } from "../../lib/api";
import { uptimeFigure } from "../../lib/dashboard";
import { dateOnly } from "../../lib/format";
import type { StationUptime, UptimeDay, UptimeWindow } from "../../lib/types";
import {
  UPTIME_ATTENTION_BELOW,
  dayLabel,
  describeUptimeDay,
  hoursLabel,
  isUptimeLow,
} from "../../lib/uptime";
import { Figure } from "../dashboard/Figure";
import { ErrorNotice } from "../ui/Feedback";
import { MicroLabel, SectionTitle } from "../ui/Text";

/**
 * How reachable this station has been: three windows as figures, and the last
 * thirty days as one bar each, in the depot's calendar.
 *
 * Fetched on its own and slowly. The page around it polls every ten seconds for
 * connector state; a month of history moves by a minute's worth in a minute.
 */

const POLL_MS = 60_000;

/** Bars this close to either edge open their tooltip inward, so it is not clipped. */
const EDGE_BARS = 5;

export function UptimeHistory({ chargePointId }: { readonly chargePointId: string }) {
  const uptime = useQuery({
    queryKey: ["charge-point-uptime", chargePointId],
    queryFn: () => get<StationUptime>(`/charge-points/${encodeURIComponent(chargePointId)}/uptime`),
    refetchInterval: POLL_MS,
  });

  return (
    // A container, so the figures follow the column this sits in rather than the
    // viewport: beside the station facts it is a quarter of the page.
    <section aria-labelledby="uptime-title" className="@container flex flex-col gap-4">
      <SectionTitle
        action={
          uptime.data === undefined ? undefined : (
            <MicroLabel>Last 30 days · {uptime.data.timeZone}</MicroLabel>
          )
        }
      >
        <span id="uptime-title">Uptime</span>
      </SectionTitle>

      {uptime.isError ? (
        <ErrorNotice error={uptime.error} onRetry={() => void uptime.refetch()} />
      ) : uptime.data === undefined ? (
        <MicroLabel>Loading</MicroLabel>
      ) : (
        <UptimeBody history={uptime.data} />
      )}
    </section>
  );
}

function UptimeBody({ history }: { readonly history: StationUptime }) {
  if (history.collectingSince === null) {
    return (
      <p className="m-0 max-w-prose text-body text-ink-muted">
        No connection history has been recorded yet. Uptime is counted from the first connection the
        server logs.
      </p>
    );
  }

  // The server measures only accepted stations in service; the thirty-day window
  // is the widest, so nothing there means nothing anywhere.
  if (history.last30d === null) {
    return (
      <p className="m-0 max-w-prose text-body text-ink-muted">
        Not measured. Uptime counts only accepted stations in service, so a station awaiting
        approval or hidden from the fleet has no figure rather than a falling one.
      </p>
    );
  }

  return (
    <>
      {/* Three abreast once each figure has room for "100 %"; stacked below that. */}
      <dl className="m-0 grid grid-cols-1 gap-3 @min-[16rem]:grid-cols-3 @min-[16rem]:gap-4">
        <WindowFigure label="24 h" window={history.last24h} />
        <WindowFigure label="7 d" window={history.last7d} />
        <WindowFigure label="30 d" window={history.last30d} />
      </dl>

      <DailyBars days={history.daily} />

      <p className="m-0 max-w-prose text-micro tracking-normal normal-case text-ink-faint">
        Days under {UPTIME_ATTENTION_BELOW * 100}% are marked in red; the number under a bar is how
        many times the station dropped its connection that day. Counted from{" "}
        {dateOnly(history.collectingSince, history.timeZone)}, when the history began.
      </p>
    </>
  );
}

function WindowFigure({
  label,
  window,
}: {
  readonly label: string;
  readonly window: UptimeWindow | null;
}) {
  return (
    <Figure
      label={label}
      value={uptimeFigure(window?.ratio ?? null)}
      unit="%"
      // Offline time rather than "x of y": the number someone acts on, and short
      // enough for three figures abreast on a phone.
      sub={
        window === null
          ? undefined
          : `${hoursLabel(window.expectedSeconds - window.connectedSeconds)} offline`
      }
    />
  );
}

function DailyBars({ days }: { readonly days: readonly UptimeDay[] }) {
  const first = days[0];

  return (
    <figure className="m-0 flex flex-col gap-1.5">
      {/* Above the plot, not on it: a full bar reaches the line this labels. */}
      <span
        aria-hidden="true"
        className="self-end text-micro tracking-normal normal-case text-ink-faint"
      >
        100%
      </span>
      <div className="relative">
        {/* The 100% line: the one gridline a ratio needs. */}
        <div className="pointer-events-none absolute inset-x-0 top-0 border-t border-dashed border-rule" />
        <ol
          aria-label="Uptime by day"
          className="m-0 flex h-28 list-none items-end gap-[2px] border-b border-rule-strong p-0"
        >
          {days.map((day, index) => (
            <DayBar
              key={day.date}
              day={day}
              align={
                index < EDGE_BARS ? "start" : index >= days.length - EDGE_BARS ? "end" : "center"
              }
            />
          ))}
        </ol>
      </div>

      <div aria-hidden="true" className="flex gap-[2px]">
        {days.map((day) => (
          <span
            key={day.date}
            className="flex-1 text-center text-micro tracking-normal normal-case text-signal tabular-nums"
          >
            {day.disconnects > 0 ? day.disconnects : ""}
          </span>
        ))}
      </div>

      <figcaption className="flex justify-between text-micro tracking-normal normal-case text-ink-faint">
        <span>{first === undefined ? "" : dayLabel(first.date)}</span>
        <span>Today</span>
      </figcaption>
    </figure>
  );
}

const TOOLTIP_ALIGN = {
  start: "left-0",
  center: "left-1/2 -translate-x-1/2",
  end: "right-0",
} as const;

function DayBar({
  day,
  align,
}: {
  readonly day: UptimeDay;
  readonly align: keyof typeof TOOLTIP_ALIGN;
}) {
  const text = describeUptimeDay(day);
  const low = isUptimeLow(day.ratio);

  return (
    <li
      // Focusable so the tooltip is reachable without a mouse; the label is the tooltip.
      // biome-ignore lint/a11y/noNoninteractiveTabindex: each bar carries a value worth reading
      tabIndex={0}
      aria-label={text}
      className="group relative flex h-full flex-1 items-end outline-none focus-visible:bg-paper-sunken hover:bg-paper-sunken"
    >
      {day.ratio === null ? (
        <span className="block h-0.5 w-full bg-rule" />
      ) : (
        <span
          className={`block w-full rounded-t-[4px] ${low ? "bg-state-faulted" : "bg-ink"}`}
          // Never shorter than a sliver: a day offline throughout is a red tick, not
          // nothing, and nothing is what "no history" looks like.
          style={{ height: `max(${day.ratio * 100}%, 2px)` }}
        />
      )}
      <span
        role="tooltip"
        className={`pointer-events-none absolute bottom-full z-10 mb-2 hidden whitespace-nowrap bg-ink px-2 py-1 text-micro tracking-normal normal-case text-paper group-hover:block group-focus-visible:block ${TOOLTIP_ALIGN[align]}`}
      >
        {text}
      </span>
    </li>
  );
}
